import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Save, ArrowLeft } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button, Card, FormField, PageHeader } from '../components/UI';

const INITIAL = {
  report_name: '', description: '', frequency: 'monthly',
  anchor_date: '', custom_cron: '',
  reminder_lead_days: '7', responsible_person: '', responsible_email: '',
  responsible_user_id: '', report_recipient: '', expiry_date: '',
  is_active: true, department_id: '',
};

const FREQS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly (every 2 weeks)' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly (every 3 months)' },
  { value: 'biannually', label: 'Bi-Annual (every 6 months)' },
  { value: 'annually', label: 'Annually' },
  { value: 'custom', label: 'Custom (Cron)' },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ANCHOR_LABELS = {
  daily: 'the day it starts',
  weekly: 'the day of the week it started',
  fortnightly: 'the day it started (repeats every 14 days from here)',
  monthly: 'the day of the month it\'s due',
  quarterly: 'when it\'s first due (repeats every 3 months from this date)',
  biannually: 'when it\'s first due (repeats every 6 months from this date)',
  annually: 'when it\'s due each year',
};

function validate(f) {
  const e = {};
  if (!f.report_name.trim()) e.report_name = 'Report name is required.';
  if (!f.description.trim()) e.description = 'Description is required.';
  if (!f.frequency) e.frequency = 'Frequency is required.';
  if (f.frequency === 'custom') {
    if (!f.custom_cron.trim()) e.custom_cron = 'Enter a cron expression.';
  } else if (!f.anchor_date) {
    e.anchor_date = 'A reference date is required — the schedule is counted forward from it.';
  }
  const lead = parseInt(f.reminder_lead_days);
  if (isNaN(lead) || lead < 0 || lead > 365) e.reminder_lead_days = 'Enter 0–365 days.';
  if (!f.responsible_person.trim()) e.responsible_person = 'Responsible person name is required.';
  if (!EMAIL_RE.test(f.responsible_email.trim())) e.responsible_email = 'Valid email required.';
  if (!f.report_recipient.trim()) e.report_recipient = 'At least one recipient is required.';
  else {
    const emails = f.report_recipient.split(',').map(e => e.trim());
    if (!emails.every(e => EMAIL_RE.test(e))) e.report_recipient = 'One or more emails are invalid.';
  }
  if (!f.expiry_date) e.expiry_date = 'Expiry date is required.';
  else if (new Date(f.expiry_date) < new Date()) e.expiry_date = 'Expiry date must be in the future.';
  return e;
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 16, paddingBottom: 8, borderBottom: '2px solid var(--primary-ghost)' }}>{title}</h3>
      {children}
    </div>
  );
}

export default function ReminderForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get('/users/departments').then(r => setDepartments(r.data)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/reminders/${id}`).then(r => {
        const d = r.data;
        setForm({
          report_name: d.report_name || '',
          description: d.description || '',
          frequency: d.frequency || 'monthly',
          anchor_date: d.anchor_date?.split('T')[0] || '',
          custom_cron: d.custom_cron || '',
          reminder_lead_days: String(d.reminder_lead_days ?? 7),
          responsible_person: d.responsible_person || '',
          responsible_email: d.responsible_email || '',
          responsible_user_id: d.responsible_user_id ?? '',
          report_recipient: d.report_recipient || '',
          expiry_date: d.expiry_date?.split('T')[0] || '',
          is_active: !!d.is_active,
          department_id: d.department_id ?? '',
        });
      }).catch(() => { toast.error('Failed to load report'); navigate('/reminders'); })
        .finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); toast.error('Please fix the form errors.'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        anchor_date: form.anchor_date || null,
        custom_cron: form.custom_cron || null,
        reminder_lead_days: parseInt(form.reminder_lead_days),
        is_active: form.is_active ? 1 : 0,
        department_id: form.department_id || null,
        responsible_user_id: form.responsible_user_id || null,
      };
      if (isEdit) { await api.put(`/reminders/${id}`, payload); toast.success('Report updated!'); }
      else { await api.post('/reminders', payload); toast.success('Report created!'); }
      navigate('/reminders');
    } catch(err) {
      const msg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.error || 'Save failed.';
      toast.error(msg);
    }
    setLoading(false);
  }

  if (fetching) return <div style={{ padding: 40 }}><div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTop: '3px solid var(--primary)', borderRadius: '50%' }} className="spin" /></div>;

  return (
    <div style={{ padding: '32px 36px', maxWidth: 860, margin: '0 auto' }} className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button onClick={() => navigate('/reminders')} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13.5, fontWeight: 600, padding: '8px 12px', borderRadius: 8, transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--border-light)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={15} /> Back
        </button>
      </div>

      <PageHeader
        title={isEdit ? 'Edit Report' : 'Create Report'}
        subtitle={isEdit ? 'Update the report reminder settings' : 'Set up a new compliance report reminder'}
      />

      <form onSubmit={handleSubmit}>
        <Card style={{ marginBottom: 20 }}>
          <Section title="Report Information">
            <FormField label="Report Name" required error={errors.report_name}>
              <input value={form.report_name} onChange={e => set('report_name', e.target.value)} placeholder="e.g. Monthly Financial Report" />
            </FormField>
            <FormField label="Description" required error={errors.description}>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="What this report is about, what it covers…" rows={3} />
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Department" error={errors.department_id}>
                <select value={form.department_id} onChange={e => set('department_id', e.target.value)}>
                  <option value="">Select department…</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </FormField>
              <FormField label="Status">
                <select value={form.is_active ? '1' : '0'} onChange={e => set('is_active', e.target.value === '1')}>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </FormField>
            </div>
          </Section>

          <Section title="Responsible Person">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Full Name" required error={errors.responsible_person}>
                <input value={form.responsible_person} onChange={e => set('responsible_person', e.target.value)} placeholder="Full name" />
              </FormField>
              <FormField label="Email Address" required error={errors.responsible_email}>
                <input type="email" value={form.responsible_email} onChange={e => set('responsible_email', e.target.value)} placeholder="email@org.com" />
              </FormField>
            </div>
            <FormField label="Link to User Account" hint="Optionally link this to a system user for access control" error={errors.responsible_user_id}>
              <select value={form.responsible_user_id} onChange={e => set('responsible_user_id', e.target.value)}>
                <option value="">Select a user (optional)…</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.email}) - {u.role}</option>)}
              </select>
            </FormField>
          </Section>

          <Section title="Frequency & Schedule">
            <FormField label="Frequency" required error={errors.frequency}>
              <select value={form.frequency} onChange={e => set('frequency', e.target.value)}>
                {FREQS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </FormField>
            {form.frequency !== 'custom' && (
              <FormField
                label="Reference Date"
                required
                error={errors.anchor_date}
                hint={`The date this report is (or was) due for ${ANCHOR_LABELS[form.frequency] || 'the schedule'}. Every future due date is counted forward from this one — e.g. a quarterly report anchored on 15 June recurs on 15 Sep, 15 Dec, 15 Mar, and so on, not fixed calendar quarters.`}
              >
                <input type="date" value={form.anchor_date} onChange={e => set('anchor_date', e.target.value)} style={{ maxWidth: 200 }} />
              </FormField>
            )}
            {form.frequency === 'custom' && (
              <FormField label="Cron Expression" required error={errors.custom_cron} hint="e.g. 0 9 1 * * = 9am on the 1st of every month">
                <input value={form.custom_cron} onChange={e => set('custom_cron', e.target.value)} placeholder="0 9 1 * *" style={{ fontFamily: 'var(--font-mono)' }} />
              </FormField>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Reminder Lead Days" required error={errors.reminder_lead_days} hint="Send reminder X days before due date">
                <input type="number" min="0" max="365" value={form.reminder_lead_days} onChange={e => set('reminder_lead_days', e.target.value)} />
              </FormField>
              <FormField label="Expiry Date" required error={errors.expiry_date} hint="Stop sending reminders after this date">
                <input type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} />
              </FormField>
            </div>
          </Section>

          <Section title="Recipients">
            <FormField label="Email Recipients" required error={errors.report_recipient} hint="Comma-separated list of email addresses who receive the reminder">
              <textarea value={form.report_recipient} onChange={e => set('report_recipient', e.target.value)} placeholder="recipient1@org.com, recipient2@org.com" rows={2} />
            </FormField>
          </Section>
        </Card>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => navigate('/reminders')} type="button">Cancel</Button>
          <Button type="submit" icon={Save} loading={loading}>{isEdit ? 'Save Changes' : 'Create Report'}</Button>
        </div>
      </form>
    </div>
  );
}
